import React, { useMemo } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { typographyScale } from "../../../../../styles/theme";
import { money, signedMoney } from "./format";
import { lineStateOn, monthlyCashRows, positionOn, timelineSummary } from "./cashTimeline";
import useCashTimeline from "./useCashTimeline";
import CashFlowStrip from "./CashFlowStrip";
import CashTimelineControls from "./CashTimelineControls";
import CashTimelineCharts from "./CashTimelineCharts";
import DashboardSwitchTable from "./DashboardSwitchTable";

// The same tokens DashboardTable / DashboardSwitchTable paint with.
const BORDER = "1px solid var(--pv-table-border, rgba(170, 180, 198, 0.6))";
const HEADER_BG = "var(--pv-table-header-bg, hsla(250, 86%, 97%, 1))";
const HEADER_TEXT = "var(--pv-table-header-text, inherit)";
const numberSx = { textAlign: "right", fontVariantNumeric: "tabular-nums" };

// "good"/"bad" color a value by its sign (cash position: healthy vs a deficit);
// leaving tone out keeps a card neutral (e.g. Contract Value, a size not a
// health signal). No value at all is always the disabled gray, regardless.
function Card({ label, value, sub, tone }) {
  const color =
    value == null
      ? "text.disabled"
      : tone === "bad"
        ? "error.main"
        : tone === "good"
          ? "success.main"
          : "text.primary";

  return (
    <Paper variant="outlined" sx={{ p: 1.5, minWidth: 0 }}>
      <Typography variant="overline" color="text.secondary" noWrap>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontVariantNumeric: "tabular-nums", color }}>
        {value == null ? "—" : value}
      </Typography>
      {sub && (
        <Typography variant="caption" color="text.secondary">
          {sub}
        </Typography>
      )}
    </Paper>
  );
}

const toneOf = (value) => (value == null ? undefined : value >= 0 ? "good" : "bad");

// Month-end cumulative Cash In / Cash Out, with Diff (the cushion) colored by
// sign: green while collections cover payouts, red the month they don't.
function CashFlowTable({ rows }) {
  return (
    <Paper variant="outlined" sx={{ overflow: "hidden" }}>
      <TableContainer sx={{ overflowX: "auto", maxHeight: 360 }}>
        <Table
          size="small"
          stickyHeader
          sx={{
            minWidth: 420,
            "& .MuiTableCell-root": {
              borderRight: BORDER,
              borderBottom: BORDER,
              px: 1,
              py: 0.75,
              fontSize: typographyScale.fontSize.sm,
              whiteSpace: "nowrap",
            },
            "& .MuiTableCell-root:last-of-type": { borderRight: 0 },
          }}
        >
          <TableHead>
            <TableRow>
              {["Month end", "Cash In", "Cash Out", "Diff"].map((head) => (
                <TableCell
                  key={head}
                  align={head === "Month end" ? "left" : "right"}
                  sx={{ bgcolor: HEADER_BG, color: HEADER_TEXT, fontSize: typographyScale.fontSize.xs, fontWeight: 700 }}
                >
                  {head}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.label} hover>
                <TableCell>{row.label}</TableCell>
                <TableCell sx={numberSx}>{money(row.cashIn)}</TableCell>
                <TableCell sx={numberSx}>{money(row.cashOut)}</TableCell>
                <TableCell
                  sx={{ ...numberSx, fontWeight: 600, color: row.diff >= 0 ? "success.main" : "error.main" }}
                >
                  {signedMoney(row.diff)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

/**
 * The switch model, day by day: where the money stands on the cursor day, the
 * five timeline charts, the month-end table, and the per-line switch table.
 * The payment-mode comparison and the scope toggles are view-only what-ifs
 * (useCashTimeline) - only the policy behind the schedule is saved.
 *
 * @param schedule computeSwitchSchedule() / buildDemoSwitchSchedule() result
 * @param flags    switchComplianceFlags() result
 */
export default function DashboardCashFlow({ schedule, flags }) {
  const {
    timeline,
    mode,
    setMode,
    excluded,
    toggleLineInScope,
    selectedCode,
    toggleSelectedLine,
    cursorDay,
    moveCursor,
    isPlaying,
    togglePlay,
  } = useCashTimeline(schedule);

  const summary = useMemo(() => timelineSummary(timeline, schedule), [timeline, schedule]);
  const monthlyRows = useMemo(() => monthlyCashRows(timeline), [timeline]);
  const lineStates = useMemo(
    () =>
      new Map(
        schedule.map((row) => [row.code, lineStateOn(row, timeline, cursorDay, excluded.has(row.code))]),
      ),
    [schedule, timeline, cursorDay, excluded],
  );
  const switchedLines = schedule.filter(
    (row) => !excluded.has(row.code) && lineStates.get(row.code).firedCount === row.packets.length,
  ).length;

  const switchTable = (
    <DashboardSwitchTable
      schedule={schedule}
      lineStates={lineStates}
      flags={flags}
      excluded={excluded}
      onToggleLineInScope={toggleLineInScope}
      selectedCode={selectedCode}
      onSelectLine={toggleSelectedLine}
    />
  );

  if (!timeline.series) {
    return (
      <Box sx={{ display: "grid", gap: 1.5 }}>
        <Box sx={{ py: 6, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            Nothing to put on a timeline yet — a line's work counts as done once
            its task reaches 100% / Completed (or, for man-hour lines, once its
            progress covers a packet), and no line in scope has a dated task.
          </Typography>
        </Box>
        {switchTable}
      </Box>
    );
  }

  return (
    <Box sx={{ display: "grid", gap: 1.5 }}>
      <CashFlowStrip position={positionOn(timeline, cursorDay)} totals={timeline.totals} />
      <CashTimelineControls
        timeline={timeline}
        cursorDay={cursorDay}
        onCursorChange={moveCursor}
        isPlaying={isPlaying}
        onTogglePlay={togglePlay}
        mode={mode}
        onModeChange={setMode}
      />

      <Box
        sx={{
          display: "grid",
          gap: 1.5,
          gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(5, minmax(0, 1fr))" },
        }}
      >
        <Card
          label="Contract Value"
          value={money(summary.contract)}
          sub={`${switchedLines} of ${schedule.length - excluded.size} lines switched on this day`}
        />
        <Card label="Planned Margin" value={money(summary.plannedMargin)} tone={toneOf(summary.plannedMargin)} />
        <Card label="Peak Cushion" value={summary.peakCushion != null ? money(summary.peakCushion) : null} tone={toneOf(summary.peakCushion)} />
        <Card
          label="Lowest Cushion"
          value={summary.lowestCushion != null ? money(summary.lowestCushion) : null}
          sub={summary.lowestCushion < -1 ? "Chinook funds the client here" : "never below zero"}
          tone={toneOf(summary.lowestCushion)}
        />
        <Card
          label="Avg. Work Done → Payout"
          value={summary.avgEarnToPayoutDays != null ? `${summary.avgEarnToPayoutDays.toFixed(1)} days` : null}
          sub="approval + client terms + vendor terms"
        />
      </Box>

      <CashTimelineCharts
        timeline={timeline}
        cursorDay={cursorDay}
        onCursorChange={moveCursor}
        selectedCode={selectedCode}
      />

      <Typography variant="caption" color="text.secondary">
        Click a row to mark its events on the money chart; untick “In scope” to
        see every figure without that line. Man-hour lines are cut into packets,
        each with its own switch — smaller packets (see “Cash flow policy”) flip
        sooner, so payroll is covered sooner.
      </Typography>
      {switchTable}
      <CashFlowTable rows={monthlyRows} />
    </Box>
  );
}
